import React from 'react';
import { useQBStore } from '@/store/qbStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, ImagePlus, X, Plus, AlertCircle } from 'lucide-react';
import { getFileUrl, uploadFile } from '@/api/qb';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LatexText } from '@/components/ui/LatexText';

export const ExtractionEditor: React.FC = () => {
  const { draftQuestions, updateDraft, removeDraft, insertDraft, isExtracting, isUploading, error } = useQBStore();

  const handleInsertQuestion = (index: number) => {
    // Generate a simple ID if crypto.randomUUID is not available
    const generateId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    };

    const newQuestion: any = {
      id: generateId(),
      type: 'single_choice',
      content: { stem: '', images: [] },
      options: [
        { label: 'A', content: '' },
        { label: 'B', content: '' },
        { label: 'C', content: '' },
        { label: 'D', content: '' },
      ],
      answer: { value: '', images: [] },
      analysis: '',
      difficulty: 3,
      knowledge_points: [],
    };
    insertDraft(index, newQuestion);
  };

  const normalizeImages = (images?: string[]) => {
    return (images || []).filter((img) => img && (img.startsWith('http') || img.startsWith('/')));
  };

  const handleImageUpload = async (
    questionIndex: number, 
    file: File, 
    target: 'content' | 'answer' | 'option' = 'content',
    optionIndex?: number
  ) => {
    try {
        const response = await uploadFile(file);
        const question = draftQuestions[questionIndex];
        const imageUrl = response.url;
        
        if (target === 'content') {
            // Append markdown image syntax to stem
            // We use simple string concatenation here. Ideally, we would insert at cursor position.
            // Since we don't have a ref to the textarea cursor, we append to the end.
            const newStem = (question.content.stem || '') + `\n![image](${imageUrl})\n`;
            
            // Keep the image in the 'images' array as well for backward compatibility and resource management
            const newImages = [...normalizeImages(question.content.images), imageUrl];
            
            updateDraft(questionIndex, {
                ...question,
                content: { 
                    ...question.content, 
                    stem: newStem,
                    images: newImages 
                }
            });
        } else if (target === 'answer') {
             // For answers, we append to the end as well
            const newValue = (question.answer.value || '') + `\n![image](${imageUrl})\n`;
            const newImages = [...normalizeImages(question.answer.images), imageUrl];
            
            updateDraft(questionIndex, {
                ...question,
                answer: { 
                    ...question.answer, 
                    value: newValue,
                    images: newImages 
                }
            });
        } else if (target === 'option' && typeof optionIndex === 'number') {
            const options = [...(question.options || [])];
            const currentOption = options[optionIndex];
            
            // Append markdown image syntax to option content
            const newContent = (currentOption.content || '') + ` ![image](${imageUrl})`;
            const newImages = [...normalizeImages(currentOption.images), imageUrl];
            
            options[optionIndex] = { 
                ...currentOption, 
                content: newContent,
                images: newImages 
            };
            updateDraft(questionIndex, { ...question, options });
        }
    } catch (error) {
        console.error("Upload failed", error);
        alert("Upload failed");
    }
  };

  if (isUploading || isExtracting) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p>{isUploading ? '正在上传文件...' : '正在智能识别题目...'}</p>
        <p className="text-xs text-gray-400">请耐心等待，大模型正在解析您的文档</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-red-500 gap-3 px-4 text-center">
        <AlertCircle className="h-10 w-10" />
        <p className="font-medium">识别过程中发生错误</p>
        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-200 max-w-md">
          {error}
        </p>
        <p className="text-xs text-gray-400">请检查文件格式或网络连接后重试</p>
      </div>
    );
  }

  if (draftQuestions.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2">
        <p>等待识别结果...</p>
        <p className="text-xs">请在左侧上传文件并点击“开始识别”</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      <div className="flex justify-center">
        <Button variant="outline" size="sm" onClick={() => handleInsertQuestion(0)}>
            <Plus className="h-4 w-4 mr-1" />
            在顶部添加题目
        </Button>
      </div>

      {draftQuestions.map((question, index) => (
        <React.Fragment key={question.id || index}>
        <Card className="relative group">
           <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => removeDraft(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Select
                value={question.type}
                onValueChange={(value) => {
                  const updates: any = { type: value };
                  // If switching to choice and no options, add defaults
                  if (value === 'choice' && (!question.options || question.options.length === 0)) {
                    updates.options = [
                      { label: 'A', content: '' },
                      { label: 'B', content: '' },
                      { label: 'C', content: '' },
                      { label: 'D', content: '' }
                    ];
                  }
                  updateDraft(index, { ...question, ...updates });
                }}
              >
                <SelectTrigger className="w-[120px] h-8">
                  <SelectValue placeholder="题型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="choice">单选题</SelectItem>
                  <SelectItem value="multi_choice">多选题</SelectItem>
                  <SelectItem value="fill_blank">填空题</SelectItem>
                  <SelectItem value="true_false">判断题</SelectItem>
                  <SelectItem value="essay">解答题</SelectItem>
                  <SelectItem value="proof">证明题</SelectItem>
                  {/* 英语题型 */}
                  <SelectItem value="reading_comp">阅读理解</SelectItem>
                  <SelectItem value="cloze">完形填空</SelectItem>
                  <SelectItem value="listening">听力</SelectItem>
                  <SelectItem value="seven_choose_five">七选五</SelectItem>
                  <SelectItem value="grammar_fill">语法填空</SelectItem>
                  <SelectItem value="matching">信息匹配</SelectItem>
                  <SelectItem value="error_correction">短文改错</SelectItem>
                  <SelectItem value="writing">写作</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-gray-400">难度: {question.difficulty}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stem */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500">题干</label>
              <Textarea 
                value={question.content.stem}
                onChange={(e) => updateDraft(index, { 
                  ...question, 
                  content: { ...question.content, stem: e.target.value } 
                })}
                className="min-h-[80px]"
              />
              {/* Image Upload Area */}
              <div className="flex flex-wrap gap-2 mt-2">
                  {question.content.images?.map((img, imgIdx) => (
                      <div key={imgIdx} className="relative group w-20 h-20 border rounded overflow-hidden">
                           <img src={getFileUrl(img)} className="w-full h-full object-cover" alt="Question Image" />
                           <button 
                              onClick={() => {
                                  const newImages = question.content.images.filter((_, i) => i !== imgIdx);
                                  updateDraft(index, { ...question, content: { ...question.content, images: newImages }});
                              }}
                              className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                           >
                              <X className="w-3 h-3" />
                           </button>
                      </div>
                  ))}
                  <label className="w-20 h-20 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
                      <ImagePlus className="w-6 h-6" />
                      <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                              if (e.target.files?.[0]) {
                                  handleImageUpload(index, e.target.files[0], 'content');
                              }
                          }}
                      />
                  </label>
              </div>

              <div className="text-sm bg-gray-50 p-2 rounded mt-1 min-h-[40px]">
                 {/* Preview with Latex rendering */}
                 <LatexText>{question.content.stem}</LatexText>
              </div>
            </div>

            {/* Options (if choice) */}
            {(question.type || '').toLowerCase() === 'choice' && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-medium text-gray-500">选项</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      const currentOptions = question.options || [];
                      const usedLabels = new Set(currentOptions.map(o => o.label));
                      let nextLabelCode = 65;
                      while (usedLabels.has(String.fromCharCode(nextLabelCode))) {
                        nextLabelCode++;
                      }
                      const nextLabel = String.fromCharCode(nextLabelCode);
                      updateDraft(index, { 
                        ...question, 
                        options: [...currentOptions, { label: nextLabel, content: '' }] 
                      });
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" /> 添加选项
                  </Button>
                </div>
                {question.options?.map((opt, optIdx) => (
                  <div key={optIdx} className="relative group/opt p-2 border rounded-md border-transparent hover:border-gray-200 transition-colors">
                    <div className="flex gap-2 items-center">
                        <div className="w-8 flex items-center justify-center bg-gray-100 rounded text-sm font-bold shrink-0 h-9">
                          {opt.label}
                        </div>
                        <Input 
                          value={opt.content}
                          onChange={(e) => {
                            const newOptions = [...(question.options || [])];
                            newOptions[optIdx] = { ...opt, content: e.target.value };
                            updateDraft(index, { ...question, options: newOptions });
                          }}
                          className="flex-1"
                        />
                         <label className="cursor-pointer text-gray-400 hover:text-blue-500 p-2 transition-colors" title="添加图片">
                             <ImagePlus className="w-4 h-4" />
                             <input 
                                 type="file" 
                                 className="hidden" 
                                 accept="image/*"
                                 onChange={(e) => {
                                     if (e.target.files?.[0]) {
                                         handleImageUpload(index, e.target.files[0], 'option', optIdx);
                                     }
                                 }}
                             />
                         </label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-gray-400 hover:text-red-500 opacity-0 group-hover/opt:opacity-100 transition-opacity"
                          onClick={() => {
                             const newOptions = [...(question.options || [])];
                             newOptions.splice(optIdx, 1);
                             // Re-label subsequent options
                             for(let i=optIdx; i<newOptions.length; i++) {
                                 newOptions[i].label = String.fromCharCode(65 + i);
                             }
                             updateDraft(index, { ...question, options: newOptions });
                          }}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Option Images */}
                    {opt.images && opt.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2 ml-10">
                             {opt.images.map((img, imgIdx) => (
                                 <div key={imgIdx} className="relative group/img w-16 h-16 border rounded overflow-hidden">
                                     <img src={getFileUrl(img)} className="w-full h-full object-cover" alt={`Option ${opt.label} Image`} />
                                     <button 
                                        onClick={() => {
                                            const newOptions = [...(question.options || [])];
                                            const currentImages = newOptions[optIdx].images || [];
                                            newOptions[optIdx] = { 
                                                ...newOptions[optIdx], 
                                                images: currentImages.filter((_, i) => i !== imgIdx) 
                                            };
                                            updateDraft(index, { ...question, options: newOptions });
                                        }}
                                        className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity"
                                     >
                                        <X className="w-3 h-3" />
                                     </button>
                                 </div>
                             ))}
                        </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Answer & Analysis */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500">答案</label>
                <Input 
                  value={question.answer.value}
                  onChange={(e) => updateDraft(index, { 
                    ...question, 
                    answer: { ...question.answer, value: e.target.value } 
                  })}
                />
                {/* Answer Images */}
                <div className="flex flex-wrap gap-2 mt-2">
                    {question.answer.images?.map((img, imgIdx) => (
                        <div key={imgIdx} className="relative group w-12 h-12 border rounded overflow-hidden">
                             <img src={getFileUrl(img)} className="w-full h-full object-cover" alt="Ans Img" />
                             <button 
                                onClick={() => {
                                    const newImages = question.answer.images.filter((_, i) => i !== imgIdx);
                                    updateDraft(index, { ...question, answer: { ...question.answer, images: newImages }});
                                }}
                                className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                             >
                                <X className="w-3 h-3" />
                             </button>
                        </div>
                    ))}
                    <label className="w-12 h-12 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
                        <ImagePlus className="w-4 h-4" />
                        <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*"
                            onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    handleImageUpload(index, e.target.files[0], 'answer');
                                }
                            }}
                        />
                    </label>
                </div>
              </div>
              <div className="space-y-1">
                 <label className="text-xs font-medium text-gray-500">解析</label>
                 <Textarea 
                    value={question.analysis || ''}
                    onChange={(e) => updateDraft(index, { ...question, analysis: e.target.value })}
                    rows={1}
                 />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-center opacity-0 hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" className="text-blue-500" onClick={() => handleInsertQuestion(index + 1)}>
                <Plus className="h-4 w-4 mr-1" />
                在此处插入新题
            </Button>
        </div>
        </React.Fragment>
      ))}
    </div>
  );
};
